import { Tooltip } from "@vibe/core";
import React, { useEffect } from "react";
import logger from "../../lib/logger";
import { getItemNames } from "../../services/monday-service";

interface DisplayValueProps {
  columnExists?: boolean;
  columnValue: any | null;
  columnText: string | null;
  columnType: string | null;
  /**
   * A board_relation's linked item ids — the ONLY thing this component can render one from.
   *
   * On API 2025-10 a populated board_relation returns `text: null` AND `value: null`; the links
   * exist solely in Monday's `BoardRelationValue { linked_item_ids }`. Both producers now carry
   * it: the subitem engine as `ScanRow.valuesByBoard[].linkedItemIds`, the items path through
   * sharedColumnsAdapter.
   *
   * Replaces a parse of `value.linkedPulseIds` that could never have worked. Its guard tested a
   * MISSPELLED key (`linkPulseIds`, no "ed"), so the `&&` short-circuited and `getItemNames` was
   * unreachable — every board_relation cell rendered blank. Correcting the spelling alone would
   * not have helped: `linkedPulseIds` is a shape Monday has stopped sending, so there was
   * nothing to parse either way.
   */
  linkedItemIds?: string[] | null;
}
const DisplayValueComponent = ({
  columnExists,
  columnValue,
  columnText,
  columnType,
  linkedItemIds,
}: DisplayValueProps) => {
  const [displayValue, setDisplayValue] = React.useState<string | null>(null);

  const formatColumnValue = async () => {
    try {
      switch (columnType) {
        case "board_relation": {
          // Driven off the ids, never off `value` — see linkedItemIds' header for why the
          // old JSON parse could not have worked on any API version this app targets.
          if (!linkedItemIds || linkedItemIds.length === 0) break;

          try {
            const response = await getItemNames(linkedItemIds);
            return setDisplayValue(response.map((item: any) => item.name).join(", "));
          } catch (lookupError: any) {
            // The links are a FACT we already hold; only their names were unavailable.
            // Blanking the cell here would repeat the original bug's symptom — a drifting
            // row showing nothing on either side — for a merely transient failure.
            logger.error("Could not resolve linked item names:", lookupError);
            return setDisplayValue(
              `${linkedItemIds.length} linked item${linkedItemIds.length === 1 ? "" : "s"}`,
            );
          }
        }

        default:
          if (columnText) {
            return setDisplayValue(columnText);
          }
      }
    } catch (error: any) {
      logger.error("Error formatting column value:", error);
    }
  };

  useEffect(() => {
    if (!columnType) {
      logger.error("Column type is not defined");
      return;
    }
  }, [columnType]);

  useEffect(() => {
    // Gated on "is there anything to render", NOT on `columnValue` alone. That older gate was
    // a THIRD independent reason board_relation cells were blank: Monday returns `value: null`
    // for a populated board_relation on API 2025-10, so the formatter was never invoked at all
    // and the branch inside it could not have run whatever it contained.
    if (columnValue || (linkedItemIds && linkedItemIds.length > 0) || columnText) {
      formatColumnValue();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnType, columnValue, columnText, linkedItemIds]);

  return (
    <div className="max-w-[150px]">
      <Tooltip
        content={`${!columnExists ? "Column does not exist on this board" : displayValue || "Empty"}`}
      >
        {/* `border-solid` is load-bearing: Preflight is disabled project-wide, so `border`
            alone sets width and colour with no style and paints nothing. */}
        <span className="inline-block overflow-hidden px-2 py-1 w-full font-mono whitespace-nowrap bg-[var(--allgrey-background-color)] rounded border border-solid border-[var(--layout-border-color)] text-[var(--primary-text-color)] text-ellipsis">
          {!columnExists ? "Column does not exist on this board" : displayValue || "Empty"}
        </span>
      </Tooltip>
    </div>
  );
};

export default DisplayValueComponent;
